<template>
  <div class="actions flex items-center gap-1">
    <div v-if="showHeaderActions" class="flex items-center">
      <button
        v-if="showPopoutButton"
        class="button transparent compact new-window--button"
        @click="popoutWindow"
      >
        <span class="ion-android-open"></span>
      </button>
      <button
      class="exit-button-wrapper mobile-view"
      v-if="showRestartChatbotButton"
      @click="showRestartChatbotModal"
    >
      <img :src="restartIcon" alt="Exit Chat" class="exit-button" />
      <div class="tooltip mobile-view">
        Restart chat
      </div>
    </button>
      <button 
        class="exit-button-wrapper mobile-view" 
        v-if="showEndChatButton" 
        @click="endChat"
      >
        <img :src="logoutIcon" alt="Exit Chat" class="exit-button"/>
        <div class="tooltip mobile-view">
          End chat
        </div>
      </button>
      <button
        class="button transparent compact close-button"
        :class="{
          'rn-close-button': isRNWebView
        }"
      >
        <span class="ion-android-close" @click="closeWindow"></span>
        <div class="close-button-tooltip">
          Close
        </div>
      </button>
    </div>
    <button 
      class="exit-button-wrapper web-view" 
      v-if="showRestartChatbotButton && !showHeaderActions"
      @click="showRestartChatbotModal"
    >
      <img :src="restartIcon" alt="Exit Chat" class="exit-button"/>
      <div class="tooltip web-view">
        Restart chat
      </div>
    </button>
    <button 
      class="exit-button-wrapper web-view" 
      v-if="showEndChatButton && !showHeaderActions" 
      @click="endChat"
    >
      <img :src="logoutIcon" alt="Exit Chat" class="exit-button"/>
      <div class="tooltip web-view">
        End chat
      </div>
    </button>
    <restart-chatbot-confirmation-modal 
      v-if="isRestartChatbotModalVisible" 
      @cancel="hideRestartChatbotModal"
      @restart="restartChatbotFlow"
      :conversation-attributes="conversationAttributes"
    />
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import { IFrameHelper, RNHelper } from 'widget/helpers/utils';
import { buildPopoutURL } from '../helpers/urlParamsHelper';
import HiverApi from 'widget/api/hiver';
import { mixin as clickaway } from 'vue-clickaway';
import configMixin from '../mixins/configMixin';
import logoutIcon from '../assets/images/logout.svg';
import restartIcon from '../assets/images/restart.svg';
import RestartChatbotConfirmationModal from './RestartChatbotConfirmationModal.vue';
import { CHATBOTS_GAINSIGHT_EVENTS, sendGainSightEvent } from '../../gainSightIntegration';

export default {
  name: 'HeaderActions',
  mixins: [clickaway, configMixin],
  components: {
    RestartChatbotConfirmationModal
  },
  props: {
    showPopoutButton: {
      type: Boolean,
      default: false
    },
    conversationAttributes: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      chatClosedClicked: false,
      mobileMenuActive: false,
      isRestartChatbotModalVisible: false,
      logoutIcon,
      restartIcon
    };
  },
  computed: {
    showEndChatButton() {
      return (
        this.conversationAttributes &&
        (this.conversationAttributes.status === 'open' || this.conversationAttributes.status === 'pending')
      );
    },
    showRestartChatbotButton() {
      return (
        this.hiverConfig.allow_chatbots_restart && 
        this.conversationAttributes.status === 'pending' && 
        this.hasAConnectedAgentBot
      );
    },
    isIframe() {
      return IFrameHelper.isIFrame();
    },
    isRNWebView() {
      return RNHelper.isRNWebView();
    },
    showHeaderActions() {
      return this.documentWidth > 0 && this.documentWidth < 667 && (this.isIframe || this.isRNWebView);
    },
    ...mapGetters({
      authToken: 'appConfig/getAuthToken',
      webChannelConfig: 'appConfig/getWebChannelConfig',
      documentWidth: 'appConfig/getDocumentWidth',
      hiverConfig: 'hiverConfig/getHiverConfig'
    })
  },
  methods: {
    ...mapActions('conversation', ['setUserLastSeen']),
    closeMobileMenu() {
      this.mobileMenuActive = false;
    },
    showRestartChatbotModal() {
      const ugId = this.hiverConfig.usergroup_id;
      const inboxId = this.conversationAttributes.inbox_id;
      
      sendGainSightEvent(CHATBOTS_GAINSIGHT_EVENTS.CHATBOT_RESTART_FLOW_CLICKED, {
        ug_id: ugId,
        sm_id: inboxId
      });
      
      this.isRestartChatbotModalVisible = true;
    },
    hideRestartChatbotModal() {
      this.isRestartChatbotModalVisible = false;
    },
    restartChatbotFlow() {
      this.hideRestartChatbotModal();
      
      const conversationId = this.conversationAttributes.id;
      const inboxId = this.conversationAttributes.inbox_id;
      
      HiverApi.restartChatbotFlow(conversationId, inboxId);
    },
    endChat() {
      const {
        webChannelConfig: { websiteToken }
      } = this;
      if (!this.chatClosedClicked) {
        this.chatClosedClicked = true;
        this.mobileMenuActive = false;
        HiverApi.toggleStatus(websiteToken).then(() => {
          this.chatClosedClicked = false;
        });
        this.setUserLastSeen();
      }
    },
    popoutWindow() {
      this.closeWindow();
      const {
        webChannelConfig: { websiteToken },
        authToken
      } = this;
      const {
        location: { origin }
      } = window;
      const popoutWindowURL = buildPopoutURL({
        origin,
        websiteToken,
        locale: this.$root.$i18n.locale,
        conversationCookie: authToken
      });
      const popoutWindow = window.open(
        popoutWindowURL,
        `webwidget_session_${websiteToken}`,
        'resizable=off,width=400,height=600'
      );
      popoutWindow.focus();
    },
    closeWindow() {
      if (IFrameHelper.isIFrame()) {
        IFrameHelper.sendMessage({
          event: 'toggleBubble'
        });
      } else if (RNHelper.isRNWebView) {
        RNHelper.sendMessage({ type: 'close-widget' });
      }
    }
  }
};
</script>
<style scoped lang="scss">
@import '~widget/assets/scss/variables.scss';

.actions {
  position: relative;
  margin-left: auto;

  span {
    color: $color-heading;
    font-size: $font-size-large;

    &.ion-android-close {
      font-size: $font-size-big;
    }
  }

  .close-button {
    display: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
  }
  .close-button:hover {
    background-color: #0F13160A;
  }
  .rn-close-button {
    display: block !important;
  }
  .mobile-actions-menu {
    display: none;
    position: absolute;
    top: 40px;
    left: -60px;
    background: #ffffff;
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.24);
    border-radius: 4px;
    width: 95px;
    min-height: 45px;
    text-align: center;
    line-height: 45px;
    font-family: 'Roboto', sans-serif;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    a {
      cursor: pointer;
    }
  }
}
  .exit-button-wrapper {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    justify-content: center;
    align-items: center;
    display: flex;
    background-color: transparent;
    &:focus,
    &:focus-visible {
      outline: none;
    }
    &:hover {
      background-color: #0F13160A;
    }
    position: relative;
  }
  button.exit-button-wrapper.mobile-view {
    margin-right: 15px;
  }
  .exit-button {
    width: 24px;      
    height: 24px;
    display: flex;
    padding: 3px;
    background-color: transparent;
    transition: background-color 0.2s;
  }
  .tooltip {
    top: 30px;
    overflow: visible;
    justify-content: center;
    border-radius: 4px;
    background: #343C45;;
    right: -50%;
    visibility: hidden;
    opacity: 0;
    color: white;
    font-size: 12px;
    position: absolute;
    display: flex;
    align-items: center;
    white-space: nowrap;
  }
  .tooltip.mobile-view {
    right: -50%;
  }

  .exit-button-wrapper:hover .tooltip {
    opacity: 1;
    visibility: visible;
  }
  .close-button-tooltip {
    top: 65px;
    position: fixed;
    overflow: visible;
    width: 50px;
    height: 26px;
    border-radius: 4px;
    background: #343C45;
    right: 10px;
    visibility: hidden;
    opacity: 0;
    color: white;
    padding-top: 7px;
    font-size: 12px;
  }
  .close-button:hover .close-button-tooltip {
    opacity: 1;
    visibility: visible;
  }
</style>
